/**
 * @oxbo/tokens — public API for the three-layer theming system.
 *
 * The CSS (tokens.css) does the actual resolution via [data-theme] and
 * [data-tenant] attributes on the document root. This module owns the
 * TypeScript contract and the imperative controller that flips those
 * attributes and persists the choice.
 */
import "./tokens.css";

export type Theme = "dark" | "light" | "auto";
export type Tenant = "oxbo" | "cardinal" | "blackpine" | "olympic";

export const THEMES: readonly Theme[] = ["dark", "light", "auto"] as const;
export const TENANTS: readonly Tenant[] = ["oxbo", "cardinal", "blackpine", "olympic"] as const;

/** Dark is the product default (control-room ergonomics). */
export const DEFAULT_THEME: Theme = "dark";
/** Olympic Perú install ships Olympic-branded by default. */
export const DEFAULT_TENANT: Tenant = "olympic";

export interface TenantBrand {
  id: Tenant;
  /** Wordmark text shown in the top bar. */
  wordmark: string;
  /** Single-letter mark shown in the brand chip. */
  initial: string;
  /** Optional logo image (served from /public); overrides the letter mark. */
  logoSrc?: string;
}

export const TENANT_BRANDS: Record<Tenant, TenantBrand> = {
  oxbo: { id: "oxbo", wordmark: "OXBO", initial: "O" },
  cardinal: { id: "cardinal", wordmark: "Cardinal Energy", initial: "C" },
  blackpine: { id: "blackpine", wordmark: "Blackpine", initial: "B" },
  olympic: { id: "olympic", wordmark: "OLYMPIC", initial: "O", logoSrc: "/olympic-logo.png" },
};

export function isTheme(v: unknown): v is Theme {
  return typeof v === "string" && (THEMES as readonly string[]).includes(v);
}
export function isTenant(v: unknown): v is Tenant {
  return typeof v === "string" && (TENANTS as readonly string[]).includes(v);
}

/** Apply theme + tenant to the document root. Pure DOM side-effect, no persistence. */
export function applyThemeAttributes(theme: Theme, tenant: Tenant, root?: HTMLElement): void {
  const el = root ?? (typeof document !== "undefined" ? document.documentElement : undefined);
  if (!el) return;
  el.setAttribute("data-theme", theme);
  el.setAttribute("data-tenant", tenant);
}
